import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import {
  ARTICLE_BLOCK_TYPES,
  LandingPageBlock,
  LandingPageBlockType,
  GallerySummary,
  ImageAsset,
  createEmptyBlock,
} from '../../models/cms-models';
import { LandingPageCodeBlockEditorComponent } from './blocks/landing-page-code-block-editor';
import { LandingPageGalleryBlockEditorComponent } from './blocks/landing-page-gallery-block-editor';
import { LandingPageHeadingBlockEditorComponent } from './blocks/landing-page-heading-block-editor';
import { LandingPageImageBlockEditorComponent } from './blocks/landing-page-image-block-editor';
import { LandingPageReferencesBlockEditorComponent } from './blocks/landing-page-references-block-editor';
import { LandingPageTextBlockEditorComponent } from './blocks/landing-page-text-block-editor';
import { LandingPageYouTubeBlockEditorComponent } from './blocks/landing-page-youtube-block-editor';

@Component({
  selector: 'cms-landing-page-blocks-editor',
  imports: [
    CommonModule,
    LandingPageCodeBlockEditorComponent,
    LandingPageGalleryBlockEditorComponent,
    LandingPageHeadingBlockEditorComponent,
    LandingPageImageBlockEditorComponent,
    LandingPageReferencesBlockEditorComponent,
    LandingPageTextBlockEditorComponent,
    LandingPageYouTubeBlockEditorComponent,
  ],
  templateUrl: './landing-page-blocks-editor.html',
  styleUrl: './landing-page-blocks-editor.css',
})
export class LandingPageBlocksEditorComponent {
  @Input() blocks: LandingPageBlock[] = [];
  @Input() hideHeader = false;
  @Input() availableImages: ImageAsset[] = [];
  @Input() availableGalleries: GallerySummary[] = [];
  @Output() blocksChange = new EventEmitter<LandingPageBlock[]>();

  readonly blockTypes = ARTICLE_BLOCK_TYPES;
  insertionIndex: number | null = null;

  openBlockTypeDialog(index: number): void {
    this.insertionIndex = index;
  }

  closeBlockTypeDialog(): void {
    this.insertionIndex = null;
  }

  insertBlock(type: LandingPageBlockType): void {
    if (this.insertionIndex === null) {
      return;
    }

    const nextBlocks = [...this.blocks];
    nextBlocks.splice(this.insertionIndex, 0, createEmptyBlock(type, this.insertionIndex));
    this.blocksChange.emit(this.reindex(nextBlocks));
    this.closeBlockTypeDialog();
  }

  updateBlock(index: number, patch: Partial<LandingPageBlock>): void {
    this.blocksChange.emit(
      this.blocks.map((block, blockIndex) => (blockIndex === index ? { ...block, ...patch } : block)),
    );
  }

  moveBlock(index: number, direction: -1 | 1): void {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= this.blocks.length) {
      return;
    }

    const reordered = [...this.blocks];
    const [block] = reordered.splice(index, 1);
    reordered.splice(targetIndex, 0, block);
    this.blocksChange.emit(this.reindex(reordered));
  }

  removeBlock(index: number): void {
    this.blocksChange.emit(this.reindex(this.blocks.filter((_, blockIndex) => blockIndex !== index)));
  }

  private reindex(blocks: LandingPageBlock[]): LandingPageBlock[] {
    return blocks.map((block, index) => ({
      ...block,
      sortOrder: index,
    }));
  }
}
